import {
	CloseButton,
	Combobox,
	InputBase,
	Loader,
	useCombobox,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	guestUserKeys,
	listGuestUsers,
} from "@/features/users/api/admin-users";
import { formatGuestUserLabel } from "@/features/users/lib/guest-user-label";

type GuestUserPickerProps = {
	value: string;
	error?: string;
	label?: string;
	description?: string;
	onPick?: (guest: { id: string; phone: string; label: string }) => void;
	onChange: (userId: string) => void;
};

export function GuestUserPicker({
	value,
	error,
	label,
	description,
	onPick,
	onChange,
}: GuestUserPickerProps) {
	const { t } = useTranslation();
	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
	});
	const [search, setSearch] = useState("");
	const [picked, setPicked] = useState<{ id: string; label: string } | null>(
		null,
	);
	const [debounced] = useDebouncedValue(search.trim(), 300);
	const canSearch = debounced.length >= 3;

	useEffect(() => {
		if (value || !picked) {
			return;
		}
		setPicked(null);
		setSearch("");
	}, [picked, value]);

	const query = useQuery({
		queryKey: guestUserKeys.list({
			search: debounced,
			page: 1,
			limit: 20,
			sort: "name",
			order: "ASC",
		}),
		queryFn: () =>
			listGuestUsers({
				search: debounced,
				page: 1,
				limit: 20,
				sort: "name",
				order: "ASC",
			}),
		enabled: canSearch,
	});

	const clear = () => {
		setPicked(null);
		setSearch("");
		onChange("");
	};

	return (
		<Combobox
			store={combobox}
			onOptionSubmit={(userId) => {
				const row = query.data?.data.find((item) => item.id === userId);
				if (!row) {
					return;
				}
				const nextLabel = formatGuestUserLabel(row);
				setPicked({ id: userId, label: nextLabel });
				setSearch(nextLabel);
				onPick?.({ id: userId, phone: row.phone, label: nextLabel });
				onChange(userId);
				combobox.closeDropdown();
			}}
		>
			<Combobox.Target>
				<InputBase
					label={label}
					description={description}
					placeholder={t("users.searchPlaceholder")}
					error={error}
					value={search}
					rightSection={
						query.isFetching ? (
							<Loader size={16} />
						) : picked ? (
							<CloseButton
								size="sm"
								aria-label={t("common.clear")}
								onMouseDown={(event) => event.preventDefault()}
								onClick={clear}
							/>
						) : (
							<Combobox.Chevron />
						)
					}
					rightSectionPointerEvents={
						picked || query.isFetching ? "all" : "none"
					}
					onChange={(event) => {
						const next = event.currentTarget.value;
						setSearch(next);
						if (picked) {
							setPicked(null);
							onChange("");
						}
						combobox.openDropdown();
						combobox.updateSelectedOptionIndex();
					}}
					onClick={() => combobox.openDropdown()}
					onFocus={() => combobox.openDropdown()}
					onBlur={() => combobox.closeDropdown()}
				/>
			</Combobox.Target>
			<Combobox.Dropdown>
				<Combobox.Options>
					{!canSearch ? (
						<Combobox.Empty>{t("users.searchPlaceholder")}</Combobox.Empty>
					) : null}
					{canSearch && query.isError ? (
						<Combobox.Empty>{t("common.loadError")}</Combobox.Empty>
					) : null}
					{canSearch && !query.isError && query.data?.data.length === 0 ? (
						<Combobox.Empty>{t("users.empty")}</Combobox.Empty>
					) : null}
					{(query.data?.data ?? []).map((row) => (
						<Combobox.Option value={row.id} key={row.id}>
							{formatGuestUserLabel(row)}
						</Combobox.Option>
					))}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
}
